import { Injectable, NgZone } from '@angular/core';
import { ConnectionStatus, Network } from '@capacitor/network';
import { ToastController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { PluginListenerHandle } from '@capacitor/core';

// Services
import { LoggerService } from '../logger/logger.service';
import { TranslationService } from '../translation/translation.service';

// Types
import { PageNgOnInitCallBack } from '../../../shared/types/common';

@Injectable({ providedIn: 'root' })
export class NetworkService {
  public connected: boolean;
  public connectedSubject: Subject<void> = new Subject<void>();

  public retryInSeconds = 0;
  public retryAttempts = 0;

  private retryInterval?: any;
  private retrySecondsDefault = 15;
  private retryAttemptsMax = 3;

  private isPresent = false;
  private networkListeners: Map<string, PluginListenerHandle> = new Map();

  constructor(
    private logger: LoggerService,
    private zone: NgZone,
    private toastController: ToastController,
    private translate: TranslationService,
  ) {
    // Workaround per inizializzare ToastController
    void this.toastController
      .create({
        animated: false,
        icon: 'wifi',
        position: 'bottom',
        color: 'primary',
        buttons: [{ icon: 'close', role: 'cancel' }],
      })
      .then((t) => {
        void t.present();
        void t.dismiss();
      });
  }

  public appStarted(): void {
    void Network.getStatus()
      .then(({ connected }: ConnectionStatus) => {
        this.zone.run(() => {
          this.logger.consoleLog('NetworkService', 'getStatus', connected);
          this.connected = connected;

          if (this.connected) {
            if (this.isPresent) {
              void this.dismissToast();
            }

            this.clearRetryCountdown(); // <-- resetta retryInSeconds e retryAttempts
            this.connectedSubject.next();
          } else {
            if (!this.isPresent) {
              void this.presentToast();
            }
          }
        });
      })
      .catch((error) => {
        this.logger.consoleError('NetworkService', 'getStatus', error);
      });

    void Network.addListener('networkStatusChange', ({ connected }: ConnectionStatus) => {
      this.zone.run(() => {
        this.logger.consoleLog('NetworkService', 'networkStatusChange', { connected });
        this.connected = connected;

        if (this.connected) {
          if (this.isPresent) {
            void this.dismissToast();
          }

          this.clearRetryCountdown();
          this.connectedSubject.next();
        } else {
          if (!this.isPresent) {
            void this.presentToast();
          }
        }
      });
    });
  }

  public async pageNgOnInit(
    from: string,
    callBack?: PageNgOnInitCallBack,
    notConnectedCallback?: PageNgOnInitCallBack,
  ): Promise<void> {
    this.logger.consoleLog('NetworkService', 'pageNgOnInit', from);

    const status = await Network.getStatus();
    this.zone.run(() => {
      this.connected = status.connected;

      if (this.connected) {
        this.logger.consoleLog(from, 'Initial network status: connected');

        // <-- AZZERA QUI il contatore di retry perché la pagina è ricaricata con connessione attiva
        this.retryAttempts = 0;
        this.clearRetryCountdown();

        callBack?.();
      } else {
        this.logger.consoleLog(from, 'Initial network status: not connected');
        notConnectedCallback?.();
        void this.presentToast(from);
        this.startRetryCountdown(from, async () => await callBack?.());
      }
    });

    if (this.networkListeners.has(from)) {
      await this.networkListeners.get(from)?.remove();
      this.networkListeners.delete(from);
    }

    const handle = await Network.addListener('networkStatusChange', ({ connected }) => {
      this.zone.run(() => {
        this.connected = connected;
        this.logger.consoleLog(from, 'networkStatusChange', connected);

        if (this.connected) {
          this.clearRetryCountdown();
          if (this.isPresent) void this.dismissToast(from);
          callBack?.();
        } else {
          if (!this.isPresent) void this.presentToast(from);
          notConnectedCallback?.();
          this.startRetryCountdown(from, async () => await callBack?.());
        }
      });
    });

    this.networkListeners.set(from, handle);
  }

  public async removeListeners(from: string): Promise<void> {
    this.logger.consoleLog(from, 'removeListeners');

    const handle = this.networkListeners.get(from);
    if (handle) {
      await handle.remove();
      this.networkListeners.delete(from);
    }

    this.clearRetryCountdown();
  }

  public startRetryCountdown(
    from: string,
    retryCallback: () => Promise<void> | void,
    seconds: number = this.retrySecondsDefault,
  ): void {
    this.clearRetryCountdown(false); // non resettiamo retryAttempts qui

    if (this.retryAttempts >= this.retryAttemptsMax) {
      this.logger.consoleLog(from, 'Max retry attempts reached — aborting');
      return;
    }

    this.retryInSeconds = seconds;
    this.retryAttempts++;
    this.logger.consoleLog(from, `startRetryCountdown — tentativo ${this.retryAttempts} di ${this.retryAttemptsMax}`);

    this.retryInterval = setInterval(async () => {
      this.retryInSeconds--;

      if (this.retryInSeconds <= 0) {
        this.clearRetryCountdown(false); // non resettiamo retryAttempts qui

        try {
          this.logger.consoleLog(from, 'Calling retryCallback');
          await retryCallback();
          this.logger.consoleLog(from, 'RetryCallback executed successfully');
          this.retryAttempts = 0; // reset a successo
        } catch (err) {
          this.logger.consoleError(from, `Retry attempt ${this.retryAttempts} failed`, err);
          if (this.retryAttempts < this.retryAttemptsMax) {
            this.startRetryCountdown(from, retryCallback);
          }
        }
      }
    }, 1000);
  }

  public clearRetryCountdown(resetAttempts: boolean = true): void {
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
      this.retryInterval = undefined;
    }
    this.retryInSeconds = 0;
    if (resetAttempts) {
      this.retryAttempts = 0;
    }
  }

  private async presentToast(from: string = 'NetworkService'): Promise<void> {
    this.logger.consoleLog(from, 'presentToast', { connected: this.connected });
    this.isPresent = true;

    try {
      await this.toastController.dismiss();
    } catch (error: unknown) {}

    const toast = await this.toastController.create({
      message: this.translate.instant('COMMON.NO_INTERNET_CONNECTION'),
      icon: 'wifi',
      position: 'bottom',
      color: 'primary',
      buttons: [
        {
          icon: 'close',
          role: 'cancel',
          handler: () => {
            this.isPresent = false;
          },
        },
      ],
    });

    void toast.present();
  }

  private async dismissToast(from: string = 'NetworkService'): Promise<void> {
    this.logger.consoleLog(from, 'dismissToast', { connected: this.connected });
    this.isPresent = false;

    try {
      await this.toastController.dismiss();
    } catch (error: unknown) {}
  }
}
